import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const chartStyle = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 11, color: "#F8FAFC" };
const TICK = { fontSize: 10, fontFamily: "JetBrains Mono" };
const CHART_MARGIN = { left: 0, right: 8 };
const FLAG = { surplus: "#00FF7F", tight: "#F59E0B", deficit: "#EF4444" };

export const TreasuryForecast = ({ city, refreshKey }) => {
  const [f, setF] = useState(null);
  useEffect(() => { setF(null); axios.get(`${API}/commons/treasury/forecast`, { params: { city } }).then((r) => setF(r.data)).catch(() => setF({ error: true })); }, [city, refreshKey]);
  if (!f) return <div className="qs-panel p-4 grid place-items-center h-40"><Loader2 className="h-5 w-5 animate-spin text-[#00FF7F]" /></div>;
  if (f.error) return null;
  const t = f.totals;
  const data = f.months.map((m) => ({ ...m, label: m.month.slice(2).replace("-", "/"), workday_cost: -m.workday_cost, floor_purchases: -m.floor_purchases, pending_proposals: -m.pending_proposals }));
  return (
    <div className="qs-panel p-4" data-testid="treasury-forecast">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00FFFF]"><TrendingUp className="h-4 w-4" /> 12-month forecast · levy income vs planned work-days & floor purchases</div>
        <div className="flex flex-wrap gap-4 font-mono text-xs">
          <span className="text-[#00FF7F]" data-testid="forecast-income">+{t.levy_income.toLocaleString()} TRY in</span>
          <span className="text-[#F59E0B]">−{t.workday_cost.toLocaleString()} · {t.work_days} work-days</span>
          <span className="text-[#EF4444]">−{t.floor_purchases.toLocaleString()} floor purchases</span>
          <span style={{ color: t.min_balance < 0 ? "#EF4444" : "#00FF7F" }} data-testid="forecast-end-balance">end {t.end_balance.toLocaleString()} TRY</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} stackOffset="sign" margin={CHART_MARGIN}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="label" stroke="#475569" tick={TICK} />
          <YAxis stroke="#475569" tick={TICK} width={62} tickFormatter={(v) => `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k`} />
          <Tooltip contentStyle={chartStyle} formatter={(v, n) => [`${Math.abs(v).toLocaleString()} TRY`, n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={0} stroke="#475569" />
          <Bar dataKey="levy_income" name="Levy income" stackId="a" fill="#00FF7F" fillOpacity={0.8} />
          <Bar dataKey="workday_cost" name="Imece work-days" stackId="a" fill="#F59E0B" fillOpacity={0.85} />
          <Bar dataKey="floor_purchases" name="Floor purchases" stackId="a" fill="#EF4444" fillOpacity={0.8} />
          <Bar dataKey="pending_proposals" name="Pending proposals" stackId="a" fill="#8A2BE2" fillOpacity={0.8} />
          <Line type="monotone" dataKey="balance_end" name="Balance" stroke="#00FFFF" strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-6 md:grid-cols-12 gap-1 mt-2" data-testid="forecast-months">
        {f.months.map((m) => (
          <div key={m.month} className="text-center text-[9px] font-mono border py-1" style={{ borderColor: FLAG[m.flag] + "66", color: FLAG[m.flag] }} title={`${m.month}: net ${m.net} · ${m.work_days} work-days · harvest ${m.harvest_kg} kg`}>
            {m.month.slice(5)}<div className="text-[8px] uppercase">{m.flag}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 space-y-1">
        {f.recommendations.map((r) => (
          <div key={r} className="flex items-start gap-2 text-xs" data-testid="forecast-recommendation">
            {t.min_balance < 0 ? <AlertTriangle className="h-3.5 w-3.5 text-[#F59E0B] shrink-0 mt-0.5" /> : <CheckCircle2 className="h-3.5 w-3.5 text-[#00FF7F] shrink-0 mt-0.5" />}<span className="text-slate-300">{r}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-600 mt-2 font-mono">assumptions · order run-rate {f.assumptions.gmv_month_try.toLocaleString()} TRY/mo ({f.assumptions.trailing_orders_90d} orders/90d) · levy {f.levy_pct}% · {f.assumptions.schedules} schedule(s), {Math.round(f.assumptions.commons_share * 100)}% of harvest via commons · work-day {f.assumptions.workday_cost_try} TRY · below-floor probability {Math.round(f.assumptions.below_floor_probability * 100)}% · auto-support capped at 30% of balance</p>
    </div>
  );
};
